import { createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Icon, Text } from '@kognitos/lattice';

export const OUTPUT_MODAL_ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
export const OUTPUT_MODAL_DEFAULT_ZOOM_INDEX = 2;

// eslint-disable-next-line no-restricted-syntax -- reason: the zoom factor belongs to one open modal and dies with it; a store would be global and would need keying per instance.
const OutputModalZoomContext = createContext(1);

/**
 * The zoom factor the modal header is currently showing. A renderer reads it
 * rather than owning its own controls, so zoom sits with the other modal
 * actions instead of in a second toolbar inside the content.
 */
export function useOutputModalZoom(): number {
  // eslint-disable-next-line no-restricted-syntax -- reason: reads the modal-scoped context above.
  return useContext(OutputModalZoomContext);
}

export function OutputModalZoomProvider({
  zoom,
  children,
}: {
  zoom: number;
  children: ReactNode;
}) {
  return (
    <OutputModalZoomContext.Provider value={zoom}>
      {children}
    </OutputModalZoomContext.Provider>
  );
}

/** Zoom out / current factor / zoom in, sized to sit beside the header icons. */
export function OutputModalZoomControl({
  zoomIndex,
  onZoomIndexChange,
}: {
  zoomIndex: number;
  onZoomIndexChange: (index: number) => void;
}) {
  const { t } = useTranslation('outputs');
  const zoom = OUTPUT_MODAL_ZOOM_LEVELS[zoomIndex];

  return (
    <Flex align="center" gap={0.5}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        disabled={zoomIndex === 0}
        onClick={() => onZoomIndexChange(Math.max(0, zoomIndex - 1))}
        aria-label={t('outputs.modal.zoomOut')}
      >
        <Icon type="ZoomOut" size="sm" />
      </Button>
      <Text
        level="small"
        className="min-w-11 text-center tabular-nums"
        aria-live="polite"
      >
        {`${Math.round(zoom * 100)}%`}
      </Text>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        disabled={zoomIndex === OUTPUT_MODAL_ZOOM_LEVELS.length - 1}
        onClick={() =>
          onZoomIndexChange(
            Math.min(OUTPUT_MODAL_ZOOM_LEVELS.length - 1, zoomIndex + 1),
          )
        }
        aria-label={t('outputs.modal.zoomIn')}
      >
        <Icon type="ZoomIn" size="sm" />
      </Button>
    </Flex>
  );
}
